// Didaktikschema für Übungen (Unterrichtsplanung + Lehrmethodik)

/** @type {readonly ("deduktiv"|"induktiv"|"kombiniert")[]} */
export const LEHRMETHODE_OPTIONS = ["deduktiv", "induktiv", "kombiniert"];

/** @type {readonly string[]} */
export const DRILL_PEDAGOGY_FIELD_KEYS = [
  "zielsetzung",
  "gruppe",
  "problemlage",
  "methodischePrinzipien",
  "lehrmethode",
  "methodischesVerfahren",
  "methodischeKonzeption",
  "durchfuehrungsorganisation",
  "methodischeHilfsmittel",
  "lehrverfahrenZielsetzung",
  "deduktivSchritte",
  "deduktivPraxisbeispiel",
  "induktivSchritte",
  "induktivPraxisbeispiel",
];

export const DEDUKTIV_SCHRITTE_VORLAGE = `1. Information zur Technik / Aufgabe:
2. Demonstration / Beschreibung des Teilziels:
3. Bewegungsanweisung:
4. Übungserklärung (Organisation):
5. Praktisches Erproben der Schüler/innen:
6. Fortlaufende Bewegungskorrektur:
7. Einbau von Variationen (Hilfsmittel):
8. Steigerung der Anforderungen:`;

export const INDUKTIV_SCHRITTE_VORLAGE = `1. Zielgerichtete Aufgabenstellung:
2. Selbständiges Finden von Lösungen durch Üben und Ausprobieren:
3. Lenken des Übungsprozesses (keine Korrektur!):
4. Herausstellen günstiger Lösungen:
5. Nachahmen und Verbessern:
6. Demonstration und Beschreibung:
7. Stellen einer neuen Bewegungsaufgabe oder Anweisungen für ein neues Teilziel:`;

const STEP_LIST_FIELD_KEYS = new Set(["deduktivSchritte", "induktivSchritte"]);

/** @param {unknown} value */
export function formatNumberedStepList(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length > 1 && lines.every((line) => /^\d+\.\s/.test(line))) {
    return lines.join("\n");
  }
  return raw.replace(/(?<=\S)\s+(?=\d+\.\s)/g, "\n");
}

function normalizeLehrmethode(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (!v) return "";
  if (
    LEHRMETHODE_OPTIONS.includes(
      /** @type {"deduktiv"|"induktiv"|"kombiniert"} */ (v),
    )
  )
    return v;
  if (v.includes("kombin") || (v.includes("dedukt") && v.includes("indukt")))
    return "kombiniert";
  if (v.includes("indukt")) return "induktiv";
  if (v.includes("dedukt")) return "deduktiv";
  return "";
}

/** @type {readonly { key: string, labelKey: string, hintKey: string, groupKey?: string, rows?: number, inputType?: "select", selectOptions?: readonly { value: string, labelKey: string }[] }[]} */
export const DRILL_PEDAGOGY_FIELDS = [
  {
    key: "zielsetzung",
    labelKey: "trainer.drills.pedagogy.zielsetzung.title",
    hintKey: "trainer.drills.pedagogy.zielsetzung.hint",
    rows: 4,
  },
  {
    key: "gruppe",
    labelKey: "trainer.drills.pedagogy.gruppe.title",
    hintKey: "trainer.drills.pedagogy.gruppe.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "problemlage",
    labelKey: "trainer.drills.pedagogy.problemlage.title",
    hintKey: "trainer.drills.pedagogy.problemlage.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "methodischePrinzipien",
    labelKey: "trainer.drills.pedagogy.methodischePrinzipien.title",
    hintKey: "trainer.drills.pedagogy.methodischePrinzipien.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "lehrmethode",
    labelKey: "trainer.drills.pedagogy.lehrmethode.title",
    hintKey: "trainer.drills.pedagogy.lehrmethode.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
    inputType: "select",
    selectOptions: LEHRMETHODE_OPTIONS.map((value) => ({
      value,
      labelKey: `trainer.drills.pedagogy.lehrmethode.${value}`,
    })),
  },
  {
    key: "methodischesVerfahren",
    labelKey: "trainer.drills.pedagogy.methodischesVerfahren.title",
    hintKey: "trainer.drills.pedagogy.methodischesVerfahren.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "methodischeKonzeption",
    labelKey: "trainer.drills.pedagogy.methodischeKonzeption.title",
    hintKey: "trainer.drills.pedagogy.methodischeKonzeption.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "durchfuehrungsorganisation",
    labelKey: "trainer.drills.pedagogy.durchfuehrungsorganisation.title",
    hintKey: "trainer.drills.pedagogy.durchfuehrungsorganisation.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "methodischeHilfsmittel",
    labelKey: "trainer.drills.pedagogy.methodischeHilfsmittel.title",
    hintKey: "trainer.drills.pedagogy.methodischeHilfsmittel.hint",
    groupKey: "trainer.drills.pedagogy.unterrichtsplanung.title",
  },
  {
    key: "lehrverfahrenZielsetzung",
    labelKey: "trainer.drills.pedagogy.lehrverfahrenZielsetzung.title",
    hintKey: "trainer.drills.pedagogy.lehrverfahrenZielsetzung.hint",
    groupKey: "trainer.drills.pedagogy.lehrmethodik.title",
    rows: 4,
  },
  {
    key: "deduktivSchritte",
    labelKey: "trainer.drills.pedagogy.deduktivSchritte.title",
    hintKey: "trainer.drills.pedagogy.deduktivSchritte.hint",
    groupKey: "trainer.drills.pedagogy.lehrmethodik.title",
    rows: 8,
    templateKey: "deduktiv",
  },
  {
    key: "deduktivPraxisbeispiel",
    labelKey: "trainer.drills.pedagogy.deduktivPraxisbeispiel.title",
    hintKey: "trainer.drills.pedagogy.deduktivPraxisbeispiel.hint",
    groupKey: "trainer.drills.pedagogy.lehrmethodik.title",
    rows: 6,
  },
  {
    key: "induktivSchritte",
    labelKey: "trainer.drills.pedagogy.induktivSchritte.title",
    hintKey: "trainer.drills.pedagogy.induktivSchritte.hint",
    groupKey: "trainer.drills.pedagogy.lehrmethodik.title",
    rows: 7,
    templateKey: "induktiv",
  },
  {
    key: "induktivPraxisbeispiel",
    labelKey: "trainer.drills.pedagogy.induktivPraxisbeispiel.title",
    hintKey: "trainer.drills.pedagogy.induktivPraxisbeispiel.hint",
    groupKey: "trainer.drills.pedagogy.lehrmethodik.title",
    rows: 6,
  },
];

/**
 * @param {"deduktiv"|"induktiv"} type
 */
export function getPedagogyStepTemplate(type) {
  if (type === "induktiv") return INDUKTIV_SCHRITTE_VORLAGE;
  return DEDUKTIV_SCHRITTE_VORLAGE;
}

export function emptyDrillPedagogy() {
  return {
    zielsetzung: "",
    gruppe: "",
    problemlage: "",
    methodischePrinzipien: "",
    lehrmethode: "",
    methodischesVerfahren: "",
    methodischeKonzeption: "",
    durchfuehrungsorganisation: "",
    methodischeHilfsmittel: "",
    lehrverfahrenZielsetzung: "",
    deduktivSchritte: "",
    deduktivPraxisbeispiel: "",
    induktivSchritte: "",
    induktivPraxisbeispiel: "",
  };
}

/**
 * @param {unknown} raw
 */
export function normalizeDrillPedagogy(raw) {
  const base = emptyDrillPedagogy();
  if (!raw || typeof raw !== "object") return base;
  const src = /** @type {Record<string, unknown>} */ (raw);
  for (const key of DRILL_PEDAGOGY_FIELD_KEYS) {
    const value = src[key];
    if (value == null || !String(value).trim()) continue;
    if (key === "lehrmethode") {
      base.lehrmethode = normalizeLehrmethode(value);
      continue;
    }
    const text = String(value).trim();
    base[key] = STEP_LIST_FIELD_KEYS.has(key)
      ? formatNumberedStepList(text)
      : text;
  }
  return base;
}

/**
 * @param {unknown} existing
 * @param {unknown} incoming
 */
export function mergeDrillPedagogy(existing, incoming) {
  const base = normalizeDrillPedagogy(existing);
  const add = normalizeDrillPedagogy(incoming);
  for (const key of DRILL_PEDAGOGY_FIELD_KEYS) {
    if (add[key]) base[key] = add[key];
  }
  return base;
}

/**
 * @param {string} value
 * @param {(key: string) => string} t
 */
export function formatLehrmethodeLabel(value, t) {
  const norm = normalizeLehrmethode(value);
  if (!norm) return String(value || "").trim();
  return t(`trainer.drills.pedagogy.lehrmethode.${norm}`);
}

/**
 * @param {Record<string, string>} pedagogy
 */
export function hasDrillPedagogyContent(pedagogy) {
  const norm = normalizeDrillPedagogy(pedagogy);
  return DRILL_PEDAGOGY_FIELD_KEYS.some((key) => norm[key]);
}
